/**
 * PyodideContext
 * Provides centralized Pyodide/Python state management via React Context
 * Manages Python runtime, power flow execution, and console output
 */

import React, {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { usePyodide, type PyodideStatus } from '@/hooks/usePyodide';
import { buildPowerFlowCode } from '@/utils/python/buildCode';
import {
  parseResultsJSON,
  getResultsSummary,
  type PowerFlowResults,
} from '@/utils/python/parseResults';
import type { Node, Connection } from '@/types';
import { log, LOG_CATEGORIES } from '@/utils/logger';

// ============================================================
// TYPES
// ============================================================

/** Console output entry */
export interface ConsoleEntry {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'code';
  message: string;
  timestamp: Date;
}

/** Power flow run options */
export interface PowerFlowOptions {
  /** Use optimal power flow instead of regular power flow */
  useOPF?: boolean;
  /** Include storage units in analysis */
  includeStorage?: boolean;
}

/** Pyodide context value */
export interface PyodideContextValue {
  // Pyodide State
  status: PyodideStatus;
  isReady: boolean;
  isLoading: boolean;
  error: string | null;
  loadingProgress: number;

  // Power Flow State
  isRunning: boolean;
  results: PowerFlowResults | null;
  lastRunTime: Date | null;

  // Console State
  consoleOutput: ConsoleEntry[];

  // Pyodide Methods
  initialize: () => Promise<void>;
  runCode: (code: string) => Promise<unknown>;

  // Power Flow Methods
  runPowerFlow: (
    nodes: Node[],
    connections: Connection[],
    options?: PowerFlowOptions
  ) => Promise<PowerFlowResults | null>;
  clearResults: () => void;

  // Console Methods
  addConsoleEntry: (type: ConsoleEntry['type'], message: string) => void;
  clearConsole: () => void;
}

// ============================================================
// CONTEXT
// ============================================================

const PyodideContext = createContext<PyodideContextValue | null>(null);

// ============================================================
// PROVIDER
// ============================================================

export interface PyodideProviderProps {
  children: ReactNode;
  /** Auto-initialize Pyodide on mount */
  autoInitialize?: boolean;
  /** Maximum console entries to keep */
  maxConsoleEntries?: number;
}

/**
 * PyodideProvider component
 * Wraps children with Pyodide/Python context
 */
export function PyodideProvider({
  children,
  autoInitialize = false,
  maxConsoleEntries = 100,
}: PyodideProviderProps): JSX.Element {
  // Use the Pyodide hook
  const pyodide = usePyodide({ autoInit: autoInitialize });

  // Power flow state
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState<PowerFlowResults | null>(null);
  const [lastRunTime, setLastRunTime] = useState<Date | null>(null);

  // Console state
  const [consoleOutput, setConsoleOutput] = useState<ConsoleEntry[]>([]);

  // Generate unique ID for console entries
  const generateEntryId = useCallback(() => {
    return `entry_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Add console entry
  const addConsoleEntry = useCallback(
    (type: ConsoleEntry['type'], message: string) => {
      const entry: ConsoleEntry = {
        id: generateEntryId(),
        type,
        message,
        timestamp: new Date(),
      };

      // Log console output as well
      try {
        if (type === 'error') {
          log.error(LOG_CATEGORIES.PYODIDE, message);
        } else if (type === 'warning') {
          log.warn(LOG_CATEGORIES.PYODIDE, message);
        } else if (type === 'success') {
          log.info(LOG_CATEGORIES.PYODIDE, message);
        } else if (type === 'code') {
          log.debug(LOG_CATEGORIES.PYODIDE, 'Executing code (truncated)', { snippet: message.slice(0, 300) });
        } else {
          log.info(LOG_CATEGORIES.PYODIDE, message);
        }
      } catch (e) {
        // ignore logging errors
      }

      setConsoleOutput((prev) => {
        const newOutput = [...prev, entry];
        // Limit console entries
        if (newOutput.length > maxConsoleEntries) {
          return newOutput.slice(-maxConsoleEntries);
        }
        return newOutput;
      });
    },
    [generateEntryId, maxConsoleEntries]
  );

  // Clear console
  const clearConsole = useCallback(() => {
    setConsoleOutput([]);
  }, []);

  // Clear results
  const clearResults = useCallback(() => {
    setResults(null);
    setLastRunTime(null);
  }, []);

  // Run power flow analysis
  const runPowerFlow = useCallback(
    async (
      nodes: Node[],
      connections: Connection[],
      options: PowerFlowOptions = {}
    ): Promise<PowerFlowResults | null> => {
      // Check if Pyodide is ready
      if (pyodide.status !== 'ready') {
        addConsoleEntry('error', 'Python runtime is not ready');
        log.warn(LOG_CATEGORIES.PYODIDE, 'Power flow requested but Pyodide not ready');
        return null;
      }

      // Validate network
      const buses = nodes.filter((n) => n.type === 'bus');
      if (buses.length === 0) {
        addConsoleEntry('error', 'Network must have at least one bus');
        log.warn(LOG_CATEGORIES.POWER_FLOW, 'Power flow cancelled: no buses');
        return null;
      }

      setIsRunning(true);
      const perfId = `powerflow-${Date.now()}`;
      log.startPerf(perfId);
      log.userAction('Power Flow Run', { nodeCount: nodes.length, connectionCount: connections.length, options });
      addConsoleEntry('info', '>>> Running power flow analysis...');

      try {
        // Build Python code
        const code = buildPowerFlowCode(nodes, connections, {
          useOPF: options.useOPF || false,
          includeStorage: options.includeStorage !== false,
          networkVar: 'n',
        });

        // Log the code being executed (truncated)
        addConsoleEntry('code', code.length > 500 ? code.slice(0, 500) + '...' : code);
        log.debug(LOG_CATEGORIES.POWER_FLOW, 'Executing Python code (truncated)', { snippet: code.slice(0, 300) });

        // Execute the code
        await pyodide.runPythonAsync(code);

        // Get results from Python globals
        const resultsJson = pyodide.pyodide?.globals.get('results_json');
        if (!resultsJson) {
          throw new Error('No results returned from power flow');
        }

        // Parse results
        const parsedResults = parseResultsJSON(resultsJson);
        setResults(parsedResults);
        setLastRunTime(new Date());

        // Log results summary
        const summary = getResultsSummary(parsedResults);
        summary.forEach((msg) => {
          if (msg.startsWith('✅')) {
            addConsoleEntry('success', msg);
          } else if (msg.startsWith('❌')) {
            addConsoleEntry('error', msg);
          } else if (msg.startsWith('⚠️')) {
            addConsoleEntry('warning', msg);
          } else {
            addConsoleEntry('info', msg);
          }
        });

        log.endPerf(perfId, LOG_CATEGORIES.POWER_FLOW, 'Power flow analysis complete', { nodeCount: nodes.length, connectionCount: connections.length });
        log.userAction('Power Flow Completed', { nodeCount: nodes.length, connectionCount: connections.length });
        log.info(LOG_CATEGORIES.POWER_FLOW, 'Power flow converged', { summary });

        return parsedResults;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        const normalized = errorMessage.toLowerCase();

        if (normalized.includes('slack') || normalized.includes('reference bus')) {
          addConsoleEntry(
            'error',
            '❌ Power flow failed: No slack bus found. Ensure at least one generator is marked as slack.'
          );
          log.warn(LOG_CATEGORIES.POWER_FLOW, 'Power flow failed: missing slack bus', {
            nodeCount: nodes.length,
            connectionCount: connections.length,
          });
        } else if (normalized.includes('singular') || normalized.includes('matrix')) {
          addConsoleEntry(
            'error',
            '❌ Power flow failed: Network has islands or numerical issues. Check connectivity and parameters.'
          );
          log.warn(LOG_CATEGORIES.POWER_FLOW, 'Power flow failed: singular matrix', {
            nodeCount: nodes.length,
            connectionCount: connections.length,
          });
        } else {
          addConsoleEntry('error', `❌ Power flow failed: ${errorMessage}`);
          log.error(
            LOG_CATEGORIES.POWER_FLOW,
            'Power flow failed: unexpected error',
            error instanceof Error ? error : undefined,
            { nodeCount: nodes.length, connectionCount: connections.length }
          );
        }
        return null;
      } finally {
        setIsRunning(false);
      }
    },
    [pyodide, addConsoleEntry]
  );

  // Computed values
  const isReady = pyodide.status === 'ready';
  const isLoading = pyodide.status === 'loading';

  // Log status changes
  React.useEffect(() => {
    log.info(LOG_CATEGORIES.PYODIDE, `Pyodide status: ${pyodide.status}`);
    if (pyodide.status === 'ready') {
      log.userAction('Pyodide Ready');
    }
    if (pyodide.status === 'error' && pyodide.error) {
      log.error(LOG_CATEGORIES.PYODIDE, 'Pyodide initialization failed', pyodide.error);
      addConsoleEntry('error', `Pyodide init failed: ${pyodide.error.message}`);
    }
  }, [pyodide.status, pyodide.error]);

  // Memoize context value
  const contextValue = useMemo<PyodideContextValue>(
    () => ({
      // Pyodide State
      status: pyodide.status,
      isReady,
      isLoading,
      error: pyodide.error ? pyodide.error.message : null,
      loadingProgress: pyodide.loadingProgress,

      // Power Flow State
      isRunning,
      results,
      lastRunTime,

      // Console State
      consoleOutput,

      // Pyodide Methods
      initialize: pyodide.initialize,
      runCode: pyodide.runPythonAsync,

      // Power Flow Methods
      runPowerFlow,
      clearResults,

      // Console Methods
      addConsoleEntry,
      clearConsole,
    }),
    [
      pyodide.status,
      pyodide.error,
      pyodide.loadingProgress,
      pyodide.initialize,
      pyodide.runPythonAsync,
      isReady,
      isLoading,
      isRunning,
      results,
      lastRunTime,
      consoleOutput,
      runPowerFlow,
      clearResults,
      addConsoleEntry,
      clearConsole,
    ]
  );

  return (
    <PyodideContext.Provider value={contextValue}>
      {children}
    </PyodideContext.Provider>
  );
}

// ============================================================
// CONSUMER HOOKS
// ============================================================

/**
 * usePyodideContext hook
 * Access Pyodide context from any component
 * 
 * @throws Error if used outside PyodideProvider
 */
export function usePyodideContext(): PyodideContextValue {
  const context = useContext(PyodideContext);

  if (!context) {
    throw new Error('usePyodideContext must be used within a PyodideProvider');
  }

  return context;
}

/**
 * usePyodideStatus hook
 * Access only Pyodide status information
 */
export function usePyodideStatus() {
  const { status, isReady, isLoading, error, loadingProgress, initialize } =
    usePyodideContext();

  return {
    status,
    isReady,
    isLoading,
    error,
    loadingProgress,
    initialize,
  };
}

/**
 * usePowerFlow hook
 * Access only power flow related state and methods
 */
export function usePowerFlow() {
  const { isRunning, results, lastRunTime, runPowerFlow, clearResults, isReady } =
    usePyodideContext();

  return {
    isRunning,
    results,
    lastRunTime,
    runPowerFlow,
    clearResults,
    isReady,
  };
}

/**
 * usePyodideConsole hook
 * Access only console related state and methods
 */
export function usePyodideConsole() {
  const { consoleOutput, addConsoleEntry, clearConsole } = usePyodideContext();

  return {
    consoleOutput,
    addConsoleEntry,
    clearConsole,
  };
}

/**
 * usePowerFlowResults hook
 * Access only power flow results
 */
export function usePowerFlowResults() {
  const { results, lastRunTime } = usePyodideContext();
  return { results, lastRunTime };
}

export default PyodideContext;
